/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-unsafe-selectors  */

/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { css, jsx } from '@compiled/react';

import Button from '@atlaskit/button/new';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Inline, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import type { FuzzyDateProps } from '../../types';

import { useDateLabel } from './use-date-label';

const wrapperStyles = css({
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	textAlign: 'left',
});
const normalHeight = css({
	'&& button': {
		paddingBlock: token('space.0'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '32px',
		height: 'auto',
		textAlign: 'left',
	},
});
const reducedHeight = css({
	'&& button': {
		paddingBlock: token('space.0'),
		height: 'auto',
		textAlign: 'left',
		alignItems: 'center',
	},
});

/**
 * These styles replicate the styling of the DatePicker component which doesn't support fuzzy date selection.
 */
const selectStyles = css({
	'&& button': {
		height: token('space.500'),
		alignItems: 'center',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border.input'),
		backgroundColor: token('color.background.input'),
		paddingInlineStart: token('space.050'),
	},
	'&& button:focus': {
		outline: 'none',
		borderColor: token('color.border.focused'),
		boxShadow: 'inset 0 0 0 1px ' + token('color.border.focused'),
	},
	'&& button::after': {
		display: 'none',
	},
	'&& button:hover': {
		backgroundColor: token('color.background.input.hovered'),
	},
	'&& button:active': {
		backgroundColor: token('color.background.input'),
	},
});

const nonClickableWrapperStyles = xcss({
	display: 'inline-flex',
	blockSize: 'min-content',
	width: 'max-content',
	height: '32px',
	paddingInlineStart: 'space.075',
	paddingInlineEnd: 'space.150',
	backgroundColor: 'color.background.neutral',
	borderRadius: token('radius.small'),
});

export const DateRange = (props: FuzzyDateProps): JSX.Element => {
	const {
		onClick,
		triggerProps = {},
		placeholder,
		value,
		withTimeScale,
		appearance,
		showIcon = true,
		showPlaceholderWithSubtleText,
	} = props;

	const dateLabel = useDateLabel({
		value,
		placeholder,
		withTimeScale,
	});

	const textColor =
		showPlaceholderWithSubtleText && !value ? 'color.text.subtlest' : 'color.text.subtle';

	if (!onClick) {
		return (
			<Inline
				alignBlock="center"
				space="space.025"
				xcss={nonClickableWrapperStyles}
				testId="date-range-trigger-wrapper"
			>
				<CalendarIcon color="currentColor" spacing="spacious" label="calendar icon" />
				<Text weight="medium" color={textColor}>
					{dateLabel}
				</Text>
			</Inline>
		);
	}

	const isSelect = appearance === 'select';

	return (
		<div
			css={[
				wrapperStyles,
				fg('preview_panel_platform_update_composer') ? reducedHeight : normalHeight,
				isSelect && selectStyles,
			]}
			data-testid="date-range-trigger-wrapper"
		>
			<Button
				{...triggerProps}
				shouldFitContainer
				onClick={onClick}
				iconBefore={() => (isSelect ? null : <CalendarIcon label="calendar icon" />)}
				iconAfter={
					showIcon
						? () =>
								isSelect ? <CalendarIcon label="" /> : <ChevronDownIcon label="" size="small" />
						: undefined
				}
				testId="date-range-trigger"
			>
				{isSelect ? (
					<Text weight={'regular'} color={textColor}>
						{dateLabel}
					</Text>
				) : showPlaceholderWithSubtleText ? (
					<Text color={textColor}>{dateLabel}</Text>
				) : (
					dateLabel
				)}
			</Button>
		</div>
	);
};
